import Editor, { OnChange, } from '@monaco-editor/react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IRootState } from '../../Storage/Redux/store';
import { SD_MonacoEditor } from '../../Utility/StaticDatas';
import { MainLoader } from '../Common';
import type { editor as EditorType } from 'monaco-editor';

function MonacoEditor({
  className,
  code,
  setCode,
  height,
  defaultLanguage,
  disabled,
}: {
  className: string;
  height?: string;
  code: string | null;
  setCode: React.Dispatch<React.SetStateAction<string | null>>;
  defaultLanguage: string;
  disabled?: boolean;
}) {
  const themeConfig = useSelector((state: IRootState) => state.themeConfig);

  const editorRef = useRef<EditorType.IStandaloneCodeEditor | null>(null);

  const [language, setLanguage] = useState<string>('');
  const [theme, setTheme] = useState<string>('vs-dark');

  useEffect(() => {
    const initialCodeContent = getInitialCode(defaultLanguage);
    setLanguage(initialCodeContent);
    setTheme(() => {
      if (themeConfig.isDarkMode) {
        return 'vs-dark';
      } else {
        return 'light';
      }
    });
  }, [defaultLanguage, themeConfig.isDarkMode]);

  useEffect(() => {
    const editor = editorRef.current;

    if (editor) {
      editor.updateOptions({ readOnly: !!disabled });
    }
  }, [disabled])


  const getInitialCode = (lang: string): string => {
    switch (lang) {
      case SD_MonacoEditor.JS:
        return '// type your JavaScript code...';
      case SD_MonacoEditor.CSS:
        return '/* type your CSS code... */';
      case SD_MonacoEditor.HTML:
        return '<!-- type your HTML code... -->';
      default:
        return '';
    }
  };

  // Function to handle editor mount
  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor;

    // Enable HTML language features
    monaco.languages.html.htmlDefaults.setOptions({
      autoClosingTags: true,
      autoIndent: true,
      format: {
        enabled: true,
      },
      suggest: {
        html5: true,
      },
    });
  };

  const handleEditorChange: OnChange = (value) => {
    const langInitialCode = getInitialCode(defaultLanguage);

    if (value?.trim() === langInitialCode.trim()) {
      setCode('');
      return;
    }
    setCode(value!);
  };

  return (
    <Editor
      theme={theme}
      className={className}
      height={height ?? "80vh"}
      defaultLanguage={defaultLanguage}
      value={code || language}
      onChange={handleEditorChange}
      onMount={handleEditorDidMount}
      loading={<MainLoader />}
    />
  );
}

export default MonacoEditor;
